import { useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import {
  ArrowRight,
  Check,
  Copy,
  HelpCircle,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react"
import { useEffect, useRef, useState, useCallback } from "react"

import { Button } from "@/components/button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import { toast } from "@/components/ui/toast"
import { authApi, getCsrfTokenFromDocumentCookie } from "@/lib/auth-api"
import { AUTH_ME_QUERY_KEY } from "@/lib/auth-query"

import { BlurFade } from "./ui/blur-fade"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { DotPattern } from "./ui/dot-pattern"
import { StepIndicator } from "./ui/step-indicator"
import { Body, TextLabel } from "./ui/typography"

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

type LoginStep = "initial" | "awaiting" | "verifying" | "complete"

const STEPS = [
  { label: "Start", description: "Generate code" },
  { label: "Copy", description: "Get challenge ID" },
  { label: "Verify", description: "Confirm in Discord" },
]

const getStepIndex = (step: LoginStep): number => {
  switch (step) {
    case "initial":
      return 0
    case "awaiting":
      return 1
    case "verifying":
      return 2
    case "complete":
      return 3
  }
}

const formatRemainingTime = (expiresAt: string | null) => {
  if (!expiresAt) {
    return null
  }

  const remainingMs = new Date(expiresAt).getTime() - Date.now()
  if (remainingMs <= 0) {
    return "Expired"
  }

  const totalSeconds = Math.ceil(remainingMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes <= 0) {
    return `${seconds}s`
  }

  return `${minutes}m ${String(seconds).padStart(2, "0")}s`
}

const getRemainingTimeTone = (expiresAt: string | null) => {
  if (!expiresAt) {
    return "text-muted-foreground"
  }

  const remainingMs = new Date(expiresAt).getTime() - Date.now()
  if (remainingMs <= 0) {
    return "text-danger"
  }

  if (remainingMs <= 60_000) {
    return "text-warning"
  }

  return "text-muted-foreground"
}

export const AuthLoginPanel = ({
  onAuthenticated,
}: {
  onAuthenticated?: () => void
}) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const mountedRef = useRef(true)

  const [step, setStep] = useState<LoginStep>("initial")
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [challengeExpiresAt, setChallengeExpiresAt] = useState<string | null>(
    null,
  )
  const [isStarting, setIsStarting] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const [didCopyChallengeCode, setDidCopyChallengeCode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [remainingTimeLabel, setRemainingTimeLabel] = useState<string | null>(
    null,
  )

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    setRemainingTimeLabel(formatRemainingTime(challengeExpiresAt))

    if (!challengeExpiresAt) {
      return
    }

    const interval = globalThis.setInterval(() => {
      const remaining = formatRemainingTime(challengeExpiresAt)
      setRemainingTimeLabel(remaining)

      // Auto-reset if expired
      if (remaining === "Expired" && mountedRef.current) {
        setError("Challenge expired. Please start a new login.")
        setStep("initial")
        setChallengeId(null)
        setChallengeExpiresAt(null)
      }
    }, 1000)

    return () => {
      globalThis.clearInterval(interval)
    }
  }, [challengeExpiresAt])

  const copyChallengeCode = useCallback(async () => {
    if (!challengeId || !globalThis.navigator?.clipboard) {
      return false
    }

    try {
      await globalThis.navigator.clipboard.writeText(challengeId)
      setDidCopyChallengeCode(true)
      setTimeout(() => setDidCopyChallengeCode(false), 1800)
      return true
    } catch {
      return false
    }
  }, [challengeId])

  const pollExchangeUntilReady = async (pendingChallengeId: string) => {
    setIsPolling(true)
    setStep("verifying")
    setError(null)

    try {
      while (mountedRef.current) {
        const csrfToken = getCsrfTokenFromDocumentCookie()
        if (!csrfToken) {
          throw new Error(
            "Missing CSRF cookie. Restart login challenge from the web app.",
          )
        }

        const exchange = await authApi.exchange({
          challengeId: pendingChallengeId,
          csrfToken,
        })

        if ("status" in exchange && exchange.status === "pending") {
          await sleep(Math.max(500, exchange.retryAfterMs))
          continue
        }

        setStep("complete")
        await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
        await router.invalidate()
        onAuthenticated?.()
        return
      }
    } finally {
      if (mountedRef.current) {
        setIsPolling(false)
      }
    }
  }

  const beginDiscordLogin = async () => {
    setIsStarting(true)
    setError(null)

    try {
      const challenge = await authApi.challenge()
      setChallengeId(challenge.challengeId)
      setChallengeExpiresAt(challenge.expiresAt)
      setStep("awaiting")

      // Auto-copy challenge code with toast feedback
      if (globalThis.navigator?.clipboard) {
        try {
          await globalThis.navigator.clipboard.writeText(challenge.challengeId)
          setDidCopyChallengeCode(true)
          setTimeout(() => setDidCopyChallengeCode(false), 1800)
          toast.success("Challenge code copied", {
            title: "Paste it in Discord with /auth complete",
          })
        } catch {
          // Clipboard access denied - user can still manually copy
        }
      }

      await pollExchangeUntilReady(challenge.challengeId)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Login failed")
      setStep("initial")
    } finally {
      if (mountedRef.current) {
        setIsStarting(false)
      }
    }
  }

  const resetFlow = () => {
    setStep("initial")
    setChallengeId(null)
    setChallengeExpiresAt(null)
    setIsStarting(false)
    setIsPolling(false)
    setError(null)
  }

  const isExpired =
    challengeExpiresAt &&
    new Date(challengeExpiresAt).getTime() - Date.now() <= 0

  return (
    <Card className="page-intro mx-auto max-w-3xl overflow-hidden">
      <DotPattern
        width={20}
        height={20}
        cx={1.5}
        cy={1.5}
        cr={1}
        className="mask-[radial-gradient(ellipse_at_80%_20%,black_10%,transparent_70%)] text-primary/30 opacity-35"
      />
      <CardHeader className="relative gap-5">
        <BlurFade delay={0} duration={0.3} offset={8} blur="6px">
          <SectionKicker icon={<Sparkles />}>Secure Access</SectionKicker>
        </BlurFade>
        <BlurFade delay={0.04} duration={0.35} offset={10} blur="8px">
          <CardTitle className="max-w-2xl text-5xl md:text-6xl">
            Sign in with Discord and step back into command
          </CardTitle>
        </BlurFade>

        {/* Step Progress Indicator */}
        <BlurFade delay={0.06} duration={0.35} offset={10} blur="8px">
          <StepIndicator
            steps={STEPS}
            currentStep={getStepIndex(step)}
            className="mt-2"
          />
        </BlurFade>
      </CardHeader>

      <CardContent className="grid gap-5 md:grid-cols-[1.1fr_0.9fr] md:items-start">
        <BlurFade delay={0.08} duration={0.35} offset={10} blur="8px">
          <div className="grid gap-4">
            <Body variant="muted">
              Generate a one-time code, confirm it in Discord, and this session
              opens as soon as the bot verifies you
            </Body>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {step === "initial" && (
                <Button
                  onClick={() => void beginDiscordLogin()}
                  disabled={isStarting}
                  size="lg"
                  className="justify-center"
                >
                  {isStarting ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="size-4" />
                      Start Discord Login
                    </>
                  )}
                </Button>
              )}

              {(step === "awaiting" || step === "verifying") && (
                <>
                  <Button
                    onClick={() => void copyChallengeCode()}
                    disabled={!challengeId || !!isExpired}
                    size="lg"
                    variant={didCopyChallengeCode ? "secondary" : "default"}
                  >
                    {didCopyChallengeCode ? (
                      <>
                        <Check className="size-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="size-4" />
                        Copy Code
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={resetFlow}
                    variant="outline"
                    size="lg"
                    aria-label="Start over"
                  >
                    <RefreshCw className="size-4" />
                    Start Over
                  </Button>
                </>
              )}
            </div>

            {/* Status indicator when polling */}
            {isPolling && (
              <BlurFade delay={0} duration={0.2} offset={4} blur="4px">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin text-primary" />
                  <span>Waiting for Discord confirmation...</span>
                </div>
              </BlurFade>
            )}

            {/* Help link */}
            <a
              href="#trouble"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              onClick={(e) => {
                e.preventDefault()
                toast.info(
                  "Make sure you're logged into Discord on the same account, then run /auth complete and paste your challenge code.",
                  { title: "Need help?" },
                )
              }}
            >
              <HelpCircle className="size-3.5" />
              Trouble logging in?
            </a>
          </div>
        </BlurFade>

        <BlurFade delay={0.12} duration={0.35} offset={10} blur="8px">
          <div className="metric-tile grid gap-3">
            <div className="flex items-center justify-between gap-3">
              <TextLabel>Challenge Code</TextLabel>
              {challengeId && remainingTimeLabel && (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${getRemainingTimeTone(challengeExpiresAt)} ${
                    isExpired
                      ? "bg-danger/10"
                      : remainingTimeLabel.startsWith("0") ||
                          (challengeExpiresAt &&
                            new Date(challengeExpiresAt).getTime() -
                              Date.now() <=
                              60_000)
                        ? "bg-warning/10"
                        : "bg-muted"
                  }`}
                >
                  {isExpired ? "Expired" : remainingTimeLabel}
                </span>
              )}
            </div>

            <p className="text-sm/6 text-muted-foreground">
              {challengeId
                ? "Copy this code, then submit it with the Discord auth command."
                : "Click 'Start Discord Login' to generate a challenge code."}
            </p>

            <div
              className={`overflow-x-auto rounded-2xl border px-4 py-3 font-mono text-sm break-all whitespace-pre-wrap shadow-inner shadow-black/5 transition-colors ${
                challengeId
                  ? "border-primary/30 bg-primary/5 text-foreground"
                  : "border-border/70 bg-(--surface-2) text-muted-foreground"
              }`}
            >
              {challengeId ?? "—"}
            </div>

            {challengeId && (
              <BlurFade delay={0} duration={0.2} offset={4} blur="4px">
                <div className="inline-card bg-card/55 px-4 py-3 text-sm/6 text-muted-foreground">
                  Run{" "}
                  <code className="rounded bg-muted px-1.5 py-0.5 font-semibold text-foreground">
                    /auth complete
                  </code>{" "}
                  in Discord, then paste this code into the
                  <span className="font-semibold text-foreground">
                    {" "}
                    challenge-id
                  </span>{" "}
                  field when prompted
                </div>
              </BlurFade>
            )}

            <div className="inline-flex items-center gap-2 text-sm font-medium text-primary">
              <ArrowRight className="size-4" />
              Discord bot confirms identity
            </div>
          </div>
        </BlurFade>

        {error && (
          <BlurFade
            delay={0}
            duration={0.25}
            offset={6}
            blur="4px"
            className="md:col-span-2"
          >
            <StatusAlert tone="danger" className="px-4 py-3">
              {error}
            </StatusAlert>
          </BlurFade>
        )}
      </CardContent>
    </Card>
  )
}
